import { List, Record, Set } from "immutable";
import * as Sentry from "@sentry/browser";
import { format, addDays, isSunday } from "date-fns";

import { defaultValues } from "@config/defaultValues";
import { IS_DEV } from "@config/consts";

import {
  BookingTasks,
  CLEANING_FREQUENCY,
  EXTRA_SERVICES_BOOKING,
  EXTRA_SERVICES_LEGACY,
  IAgent,
  IAgentRaw,
  PricePlan,
  Task,
  CleaningProductsType,
  CleaningProductRaw,
  NonStandardFrequencyShifts,
  MoreOftenFrequencyTimeSlots,
  FrequenciesDto,
} from "@typings/booking";
import { AVAILABLE_CURRENCIES, VAT_INFO } from "@typings/globals";
import { JobRaw } from "@typings/jobs";
import { TYPE_OF_SUBPARTS } from "@typings/priceSubparts";
import { LOADING_STATE } from "@typings/reduxThunkActions";
import { AvailabilitiesResponse } from "@typings/bookingAvailabilities";
import { AvailabilitiesCalendarV3 } from "@typings/bookingCalendarV3";

import {
  generateStaticBookingHours,
  generateStaticDaysInRange,
} from "@services/StaticTimeGenerators";
import { resolveCountryFromUrl } from "@services/ResolveCountryFromUrl";
import {
  DATE_FORMAT,
  getDayFromDateTimeString,
  extractHourMinute,
} from "@services/DateFormatter";

import { getConfigValue } from "@hooks/useConfig";

import {
  BookingPricing,
  BookingPricingRaw,
  BookingReducer,
} from "./booking.typings";

const {
  booking: { tasks, cleaningFrequency },
} = defaultValues;

const defaultDuration = getConfigValue(resolveCountryFromUrl(), [
  "booking",
  "defaultDuration",
]);
const defaultBedrooms = getConfigValue(resolveCountryFromUrl(), [
  "booking",
  "defaultBedrooms",
]);
const defaultBathrooms = getConfigValue(resolveCountryFromUrl(), [
  "booking",
  "defaultBathrooms",
]);

export const taskFactory = (input: Task) => Record<Task>({ ...input })();

export const bookingTasks = Record<BookingTasks>({
  [EXTRA_SERVICES_BOOKING.INTERIOR_WINDOWS]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.INTERIOR_WINDOWS,
    duration: tasks[EXTRA_SERVICES_BOOKING.INTERIOR_WINDOWS].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.INTERIOR_WINDOWS].selected,
  }),
  [EXTRA_SERVICES_BOOKING.IRONING]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.IRONING,
    duration: tasks.IRONING.duration,
    selected: tasks.IRONING.selected,
  }),
  [EXTRA_SERVICES_BOOKING.LAUNDRY_WASH_AND_DRY]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.LAUNDRY_WASH_AND_DRY,
    duration: tasks[EXTRA_SERVICES_BOOKING.LAUNDRY_WASH_AND_DRY].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.LAUNDRY_WASH_AND_DRY].selected,
  }),
  [EXTRA_SERVICES_BOOKING.INSIDE_OVEN]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.INSIDE_OVEN,
    duration: tasks[EXTRA_SERVICES_BOOKING.INSIDE_OVEN].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.INSIDE_OVEN].selected,
  }),
  [EXTRA_SERVICES_BOOKING.INSIDE_CABINETS]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.INSIDE_CABINETS,
    duration: tasks[EXTRA_SERVICES_BOOKING.INSIDE_CABINETS].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.INSIDE_CABINETS].selected,
  }),
  [EXTRA_SERVICES_BOOKING.INSIDE_FRIDGE]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.INSIDE_FRIDGE,
    duration: tasks[EXTRA_SERVICES_BOOKING.INSIDE_FRIDGE].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.INSIDE_FRIDGE].selected,
  }),
  [EXTRA_SERVICES_BOOKING.HOME_CLEANING]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.HOME_CLEANING,
    duration:
      defaultDuration || tasks[EXTRA_SERVICES_BOOKING.HOME_CLEANING].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.HOME_CLEANING].selected,
    bedrooms: defaultBedrooms || defaultValues.booking.bedroomNo,
    bathrooms: defaultBathrooms || defaultValues.booking.bathroomNo,
  }),
  [EXTRA_SERVICES_BOOKING.BONUS_15]: taskFactory({
    service: EXTRA_SERVICES_BOOKING.BONUS_15,
    duration: tasks[EXTRA_SERVICES_BOOKING.BONUS_15].duration,
    selected: tasks[EXTRA_SERVICES_BOOKING.BONUS_15].selected,
    adminOnly: true,
  }),
})();

export const bookingFromJobTemplateFactory = (
  input: JobRaw,
  previousTasks: Record<BookingTasks>,
) => {
  let extrasTime = 0;
  const updatedTasks = input.extraServices
    // @ToDo: remove this enum value when "Micheal Owen gate" gets fixed
    .map(({ type, ...rest }) => ({
      ...rest,
      service:
        type === EXTRA_SERVICES_LEGACY.INSIDE_OWEN
          ? EXTRA_SERVICES_BOOKING.INSIDE_OVEN
          : type,
    }))
    .reduce((acc: Record<BookingTasks>, item: Task) => {
      extrasTime += item.duration;

      return acc.mergeIn([item.service], {
        duration: item.duration,
        selected: true,
      });
    }, previousTasks);

  const updatedTasksWithHomeCleaning = updatedTasks.mergeIn(
    [EXTRA_SERVICES_BOOKING.HOME_CLEANING],
    {
      duration: input.duration - extrasTime,
      bedrooms: input.bedrooms,
      bathrooms: input.bathrooms,
    },
  );

  const execution: BookingReducer["execution"] = (() => {
    const splitted = input.executionDate.split("T");
    return {
      date: splitted?.[0] || null,
      time: splitted?.[1] || null,
    };
  })();

  return {
    ...input,
    execution: execution,
    preselectedAgentUuid: input.agentUuid,
    tasks: updatedTasksWithHomeCleaning,
  };
};

export const bookingAvailabilitiesCalendarInitialState: AvailabilitiesCalendarV3 =
  {
    state: LOADING_STATE.IDLE,
    availableDays: [],
    days: [],
    hours: {},
    period: {
      startDate: "",
      endDate: "",
    },
    previousStartDate: null,
    nextStartDate: null,
    defaultSelection: null,
    hasAvailabilityInCurrentPeriod: false,
    hasAvailabilityInPlaningWindow: false,
    isTouched: false,
  };

/**
 * The /booking/availability endpoint only returns days with available agents.
 * Because of that we need to generate the calendar days on the frontend
 * and then update them with the availability data from the API.
 */
export const bookingAvailabilitiesFactory = (
  input: AvailabilitiesResponse,
  hasStartDate: boolean,
): Record<AvailabilitiesCalendarV3> => {
  const { availabilities, hasAvailabilityInScannedPeriod, ...data } = input;

  if (!data.period || !data.period.startDate) {
    if (!IS_DEV) {
      Sentry.captureMessage(
        "bookingAvailabilitiesV3Factory - period is missing",
        {
          level: "log",
          extra: {
            ...data,
            hasAvailabilityInScannedPeriod,
            availabilities: availabilities.length,
          },
        },
      );
    }

    return Record(bookingAvailabilitiesCalendarInitialState)({
      ...data,
    });
  }

  const availableDays: AvailabilitiesCalendarV3["availableDays"] = [];
  const days: AvailabilitiesCalendarV3["days"] = generateStaticDaysInRange(
    data.period.startDate,
    data.period.endDate,
  ).map(date => ({
    date,
    available: availabilities.some(
      item => item.day === date && item.hours.length,
    ),
  }));
  const hours: AvailabilitiesCalendarV3["hours"] = days
    .filter(day => day.available)
    .reduce(
      (acc, curr) => ({
        ...acc,
        [curr.date]: generateStaticBookingHours(),
      }),
      {},
    );

  availabilities.map(day => {
    if (!day.hours.length) return;

    availableDays.push(day.day);

    day.hours.map(hour => {
      const key = day.day;
      const index = hours[key].findIndex(item => item.time === hour.time);

      hours[key][index] = {
        time: hour.time,
        availableAgentsRemaining: hour.availableAgentsRemaining || 3,
        // availableAgentsRemaining is an optional API field,
        // which is returned only if there's less than 3 agents
        // instead of having an optional field on the frontend
        // we stub it with relevant value
      };
    });
  });

  return Record(bookingAvailabilitiesCalendarInitialState)({
    ...data,
    availableDays,
    days,
    hours,
    hasAvailabilityInPlaningWindow: !hasStartDate
      ? hasAvailabilityInScannedPeriod
      : true,
    hasAvailabilityInCurrentPeriod: !!availableDays.length,
  });
};

export const pricingInitialState = {
  pricesPerFrequency: List(
    Object.keys(CLEANING_FREQUENCY).map(key => ({
      frequency: key as CLEANING_FREQUENCY,
      amount: 0,
      selected: key === cleaningFrequency,
    })),
  ),
  displayedPriceForFirstBooking: 0,
  displayedHoursForFirstBooking: 0,
  currency: AVAILABLE_CURRENCIES.CHF,
  hasSpecialPricing: false,
  specialPricingTranslatedMessage: "",
  totalCheckoutPrice: null,
  totalServiceVouchers: null,
  firstCleaningPrice: 0,
  discountsToDisplay: List(),
  basePricePerHour: 0,
};

export const pricingFactory = (
  input: BookingPricingRaw,
): Record<BookingPricing> => {
  const {
    oncePricePerHour,
    weeklyPricePerHour,
    every2WeeksPricePerHour,
    every4WeeksPricePerHour,
    totalCheckoutPrice,
    moreOftenPricePerHour,
    discountsToDisplay,
    ...rest
  } = input;

  const factoryInput: BookingPricing = {
    ...rest,
    pricesPerFrequency: List([
      {
        frequency: CLEANING_FREQUENCY.MORE_OFTEN,
        amount: moreOftenPricePerHour,
      },
      {
        frequency: CLEANING_FREQUENCY.WEEKLY,
        amount: weeklyPricePerHour,
      },
      {
        frequency: CLEANING_FREQUENCY.EVERY_2_WEEKS,
        amount: every2WeeksPricePerHour,
      },
      {
        frequency: CLEANING_FREQUENCY.EVERY_4_WEEKS,
        amount: every4WeeksPricePerHour,
      },
      {
        frequency: CLEANING_FREQUENCY.ONCE,
        amount: oncePricePerHour,
      },
    ]),
    totalCheckoutPrice:
      typeof totalCheckoutPrice !== "undefined" ? totalCheckoutPrice : null,
    discountsToDisplay: List(
      (Array.isArray(discountsToDisplay) ? discountsToDisplay : []).map(
        ({ type, ...subpart }) => ({
          ...subpart,
          type: type as TYPE_OF_SUBPARTS,
          currency: rest.currency,
        }),
      ),
    ),
  };

  return Record<BookingPricing>(pricingInitialState)(factoryInput);
};

export const pricePlansFactory = (
  plans: PricePlan[],
): List<Record<PricePlan>> => {
  return List(plans.map(plan => Record(pricePlanInitialState)(plan)));
};

const agentFactory = (agent: IAgentRaw): Record<IAgent> =>
  Record({
    firstName: "",
    lastName: "",
    uuid: "",
    isPrevious: false,
    label: "",
    phoneNumber: "",
    profilePictureUrl: "",
    doesIroning: false,
  })({
    ...agent,
    label: `${agent.firstName} ${agent.lastName}`,
  });

export const agentsFactory = (agents: IAgentRaw[]): List<Record<IAgent>> =>
  List(agents.map(agentFactory));

export const pricePlanFactory = (
  selectedFrequency: CLEANING_FREQUENCY,
  availablePlans: List<Record<PricePlan>>,
  serviceLocationId?: number,
): string | null => {
  // if user has active subscription we should assign ONCE jobs
  // to subscription with `useForOnceBooking` flag
  if (selectedFrequency === CLEANING_FREQUENCY.ONCE && serviceLocationId) {
    return (
      availablePlans.find(plan => plan.get("useForOnceBooking"))?.get("uuid") ||
      null
    );
  }

  const allAvailablePlansForFrequency = availablePlans.filter(
    plan => plan.get("frequency") === selectedFrequency,
  );

  return allAvailablePlansForFrequency.get(0)?.get("uuid") || null;
};

const pricePlanInitialState: PricePlan = {
  uuid: "",
  frequency: "",
  name: "",
  description: "",
  commitmentMonthsDuration: 0,
  freeCancellations: 0,
  pricePerHour: 0,
  hidden: false,
  useForOnceBooking: false,
  currency: AVAILABLE_CURRENCIES.CHF,
  pricePerHourVatInfo: VAT_INFO.NO_INFO,
};

export const serviceVoucherPlanFactory = (
  plans: PricePlan[],
): List<Record<PricePlan>> => {
  const defaultPlan = Record(pricePlanInitialState);

  if (!plans.length) {
    return List([]);
  }

  return List(plans.map(plan => defaultPlan(plan)));
};

export const registerFormInitialState = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  address: "",
  serviceLocationId: 0,
  floorAndDoor: "",
  entryCode: "",
  phoneNumber: "",
  serviceVoucher: "",
  marketingConsent: false,
};

export const cleaningProductsFactory = (
  products: CleaningProductRaw[],
): CleaningProductsType => {
  if (!Array.isArray(products) || !products.length) return List();

  return List(
    products.map(({ currency, ...rest }) =>
      Record({
        ...rest,
        currency: AVAILABLE_CURRENCIES[currency],
      })(),
    ),
  );
};

export const selectedCleaningProductsIdsFactory = (
  selectedPlan: Record<PricePlan> | undefined,
  cleaningProducts: CleaningProductsType,
  selectedCleaningProductsIds: Set<string>,
): Set<string> => {
  if (!cleaningProducts || cleaningProducts.isEmpty()) {
    return selectedCleaningProductsIds;
  }

  const vipCleaningProductsIds = cleaningProducts
    ?.filter(item => item.get("specialPrice") !== null)
    .map(item => item.get("productId"));

  return Set(vipCleaningProductsIds).union(selectedCleaningProductsIds);
};

interface PreparedTask {
  service: EXTRA_SERVICES_BOOKING | EXTRA_SERVICES_LEGACY;
  duration: number;
}

/**
 * Function that changes the duration of tast/extra services with special rules/conditions
 * @param tasks - Immutable List with tasks
 * @param rules - Array with rules to change duration time in specific task
 * @param rulesItem.flag - It's a flag to control if the rule will be applied
 * @param rulesItem.hasService - The rule will be applied if in the tasks exist this type of service
 * @param rulesItem.targetService - The rule will be applied for this service
 * @param rulesItem.subtract - Value to subtrack from the task duration
 * @param rulesItem.newValue - Value to replace the original task duration
 * @returns - Function for mapping tasks with rules
 */
export const prepareTaskFactory = (
  tasks?: List<Record<Task>>,
  rules?: {
    flag: boolean;
    hasService: EXTRA_SERVICES_BOOKING;
    targetService: EXTRA_SERVICES_BOOKING;
    subtract?: number;
    newValue?: number;
  }[],
) => {
  return function (task: Record<Task>): PreparedTask {
    const newTask = {
      service: task.get("service"),
      duration: task.get("duration"),
    };
    const rule =
      rules?.find(rule => rule.targetService === task.get("service")) || false;

    if (!rule || !rule.flag || !tasks) {
      return newTask;
    }

    const existServiceInTasks =
      List.isList(tasks) &&
      !tasks
        .filter((item: Record<Task>) => item.get("service") === rule.hasService)
        ?.isEmpty();

    if (!existServiceInTasks) return newTask;

    return {
      ...newTask,
      duration: (() => {
        if (typeof rule.newValue === "number") {
          return rule.newValue;
        }
        if (typeof rule.subtract === "number") {
          return task.get("duration") - rule.subtract;
        }

        return task.get("duration");
      })(),
    };
  };
};

export const transformTaskServiceFactory = (
  instructions: {
    toReplace: EXTRA_SERVICES_BOOKING | EXTRA_SERVICES_LEGACY;
    replaceBy: EXTRA_SERVICES_BOOKING | EXTRA_SERVICES_LEGACY;
  }[],
) => {
  return function (task: PreparedTask): PreparedTask {
    const instruction = instructions.find(
      item => item.toReplace === task.service,
    );

    if (!instruction) return task;

    return {
      ...task,
      service: instruction.replaceBy,
    };
  };
};

export const transformNonStandardToMoreOftenFrequencyFactory = ({
  shift,
  ...rest
}: NonStandardFrequencyShifts): MoreOftenFrequencyTimeSlots => {
  return {
    ...rest,
    startTime: shift,
  };
};

interface GetMoreOftenStartDatesReturnValue {
  allowedMinDate: Date;
  defaultDate: Date;
  defaultFormattedCalendarDate: string;
}

export const getMoreOftenStartDates = (): GetMoreOftenStartDatesReturnValue => {
  const allowedMinDate = addDays(new Date(), 3);
  const isDefaultDaySunday = isSunday(allowedMinDate);
  const defaultDate = isDefaultDaySunday
    ? addDays(allowedMinDate, 1)
    : allowedMinDate;

  const defaultFormattedCalendarDate = format(
    defaultDate,
    DATE_FORMAT.DAY_PICKER_INPUT,
  );

  return {
    allowedMinDate,
    defaultDate,
    defaultFormattedCalendarDate,
  };
};

export const frequenciesFactory = (
  input: FrequenciesDto,
): List<CLEANING_FREQUENCY> => {
  return List(input.availableFrequencies);
};

const getAnyDateAvailable = (
  hours: AvailabilitiesCalendarV3["hours"],
): BookingReducer["execution"] | null => {
  for (const key in hours) {
    const availableHour = hours[key].find(
      hour => hour.availableAgentsRemaining !== 0,
    );

    if (!availableHour) continue;

    return {
      date: key,
      time: availableHour.time,
    };
  }

  return null;
};

export const getNewExecutionDateTime = (
  calendar: Record<AvailabilitiesCalendarV3>,
  execution: BookingReducer["execution"],
  defaultSelection: string | null,
  isTouched: boolean,
): BookingReducer["execution"] => {
  const anyDateAvailable = getAnyDateAvailable(calendar.get("hours"));

  // save current executionDate if it's available
  if (
    isTouched &&
    !!execution.date &&
    !!execution.time &&
    !!calendar
      .get("hours")
      ?.[execution.date]?.find(
        hour =>
          hour.availableAgentsRemaining !== 0 && hour.time === execution.time,
      )
  ) {
    return execution;
  }

  if (!defaultSelection && !!anyDateAvailable) {
    return anyDateAvailable;
  }

  // workaround for the bug when the defaultSelection has T00:00:00
  // and there's no available hours for that day
  if (defaultSelection?.includes("T00:00:00")) {
    const defaultSelectionDay = getDayFromDateTimeString(defaultSelection);

    const availableHour = calendar
      .get("hours")
      ?.[defaultSelectionDay]?.find(
        hour => hour.availableAgentsRemaining !== 0,
      );
    // if the defaultSelection day is available
    if (availableHour) {
      return {
        time: availableHour.time,
        date: defaultSelectionDay,
      };
    } else if (anyDateAvailable) {
      return anyDateAvailable;
    }
  }

  return {
    date: defaultSelection?.split("T")[0] || null,
    time: extractHourMinute(defaultSelection),
  };
};
